import React from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Button,
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface UploadProgressProps {
  status: 'idle' | 'uploading' | 'success' | 'error';
  progress: number;
  deckId?: string;
  error?: string;
}

export const UploadProgress: React.FC<UploadProgressProps> = ({
  status,
  progress,
  deckId,
  error,
}) => {
  const navigate = useNavigate();

  if (status === 'idle') {
    return null;
  }

  return (
    <Paper sx={{ p: 3 }}>
      {status === 'uploading' && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Uploading...
          </Typography>
          <LinearProgress 
            variant={progress > 0 ? 'determinate' : 'indeterminate'} 
            value={progress}
            sx={{ mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            {progress > 0 ? `${Math.round(progress)}% complete` : 'Please wait...'}
          </Typography>
        </Box>
      )}

      {status === 'success' && deckId && (
        <Alert 
          severity="success" 
          icon={<CheckCircle />}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate(`/analysis/${deckId}`)}
            >
              View Details
            </Button>
          }
        >
          <Typography variant="body1" fontWeight={600}>
            Upload Successful!
          </Typography>
          <Typography variant="body2">
            Your presentation is being analyzed. You can view the progress on the analysis page.
          </Typography>
        </Alert>
      )}

      {status === 'error' && (
        <Alert 
          severity="error" 
          icon={<ErrorIcon />}
        >
          <Typography variant="body1" fontWeight={600}>
            Upload Failed
          </Typography>
          <Typography variant="body2">
            {error || 'An error occurred during upload. Please try again.'}
          </Typography>
        </Alert>
      )}
    </Paper>
  );
};

